import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, Sparkles, X } from 'lucide-react';
import { getAiLeadsSummary, getAllAiLeadsSummaryRows } from '../../services/api/implementation/impl';
import { AiCardShell, AiInsightError, AiInsightSkeleton, resolveAiProjectId } from './AiUiShared';
import {
  buildAiLeadsListPath,
  collectClientFilterLeadIds,
  isClientAiFilter
} from './aiListDeepLinks';

const CACHE_STALE_MS = 5 * 60 * 1000;

function SummaryChip({ label, onClick, tone = 'neutral' }) {
  const tones = {
    hot: 'text-red-700 ring-red-100 hover:bg-red-50',
    warm: 'text-amber-800 ring-amber-100 hover:bg-amber-50',
    cold: 'text-sky-800 ring-sky-100 hover:bg-sky-50',
    nurture: 'text-orange-800 ring-orange-100 hover:bg-orange-50',
    futureProspect: 'text-teal-800 ring-teal-100 hover:bg-teal-50',
    approval: 'text-amber-800 ring-amber-100 hover:bg-amber-50',
    uc: 'text-violet-800 ring-violet-100 hover:bg-violet-50',
    overdue: 'text-orange-800 ring-orange-100 hover:bg-orange-50',
    neutral: 'text-gray-700 ring-gray-100 hover:bg-gray-50'
  };

  const className = `rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold ring-1 ${tones[tone] || tones.neutral}`;

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {label}
      </button>
    );
  }

  return <span className={className}>{label}</span>;
}

export default function LeadsAiSummaryBanner({
  projectId,
  className = 'mx-6 mb-4',
  pipelineCounts = null
}) {
  const navigate = useNavigate();
  const apiProjectId = resolveAiProjectId(projectId);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['aiLeadsSummary', apiProjectId],
    queryFn: () => getAiLeadsSummary(apiProjectId),
    staleTime: CACHE_STALE_MS
  });

  const banner = data?.banner;
  const counts = useMemo(
    () => ({
      hot: pipelineCounts?.hot ?? banner?.hotCount ?? 0,
      warm: pipelineCounts?.warm ?? banner?.warmCount ?? 0,
      cold: pipelineCounts?.cold ?? banner?.coldCount ?? 0,
      nurture: pipelineCounts?.nurture ?? banner?.nurtureCount ?? 0,
      futureProspect:
        pipelineCounts?.futureProspect ?? banner?.futureProspectCount ?? 0
    }),
    [banner, pipelineCounts]
  );

  if (isLoading) {
    return (
      <div className={className}>
        <AiCardShell className="!p-4">
          <AiInsightSkeleton lines={3} />
        </AiCardShell>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={className}>
        <AiInsightError
          message={error?.response?.data?.message || error?.message}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!banner) return null;

  const highlights = Array.isArray(banner.highlights) ? banner.highlights : [];

  const goToLeads = (aiFilter) => {
    navigate(buildAiLeadsListPath({ aiFilter, projectId: apiProjectId }));
  };

  const chips = [];
  if (counts.hot > 0) {
    chips.push({
      key: 'hot',
      label: `${counts.hot} hot`,
      tone: 'hot',
      onClick: () => goToLeads('hot')
    });
  }
  if (counts.warm > 0) {
    chips.push({
      key: 'warm',
      label: `${counts.warm} warm`,
      tone: 'warm',
      onClick: () => goToLeads('warm')
    });
  }
  if (counts.cold > 0) {
    chips.push({
      key: 'cold',
      label: `${counts.cold} cold`,
      tone: 'cold',
      onClick: () => goToLeads('cold')
    });
  }
  if (counts.nurture > 0) {
    chips.push({
      key: 'nurture',
      label: `${counts.nurture} nurture`,
      tone: 'nurture',
      onClick: () => goToLeads('nurture')
    });
  }
  if (counts.futureProspect > 0) {
    chips.push({
      key: 'future-prospect',
      label: `${counts.futureProspect} future prospect`,
      tone: 'futureProspect',
      onClick: () => goToLeads('future-prospect')
    });
  }
  if (banner.approvalPendingCount > 0) {
    chips.push({
      key: 'approval',
      label: `${banner.approvalPendingCount} approval pending`,
      tone: 'approval',
      onClick: () => goToLeads('approval')
    });
  }
  if (banner.ucPendingCount > 0) {
    chips.push({
      key: 'uc',
      label: `${banner.ucPendingCount} UC pending`,
      tone: 'uc',
      onClick: () => goToLeads('uc')
    });
  }
  if (banner.followUpOverdueCount > 0) {
    chips.push({
      key: 'overdue',
      label: `${banner.followUpOverdueCount} overdue follow-up`,
      tone: 'overdue',
      onClick: () => goToLeads('overdue')
    });
  }

  return (
    <div className={className}>
      <AiCardShell className="!p-4">
        <div className="mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-600" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-900">AI Brief</h3>
        </div>

        <p className="text-sm font-semibold leading-snug text-gray-900">{banner.headline}</p>

        {!highlights.length && banner.summary ? (
          <p className="mt-2 text-xs leading-relaxed text-gray-600">{banner.summary}</p>
        ) : null}

        {highlights.length > 0 ? (
          <ul className="mt-3 space-y-2">
            {highlights.slice(0, 3).map((item) => (
              <li key={`${item.leadId ?? item.leadName}-${item.hint}`} className="flex gap-2 text-xs text-gray-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                <span>
                  <span className="font-semibold text-gray-900">{item.leadName}</span>
                  <span className="text-gray-500"> — {item.hint}</span>
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {chips.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2 border-t border-indigo-100/80 pt-3">
            {chips.map((chip) => (
              <SummaryChip
                key={chip.key}
                label={chip.label}
                tone={chip.tone}
                onClick={chip.onClick}
              />
            ))}
          </div>
        ) : null}
      </AiCardShell>
    </div>
  );
}

function buildBriefChips(banner, counts, onGo) {
  const chips = [];
  if (counts.hot > 0) chips.push({ key: 'hot', label: `${counts.hot} hot`, tone: 'hot', onClick: () => onGo('hot') });
  if (counts.warm > 0) chips.push({ key: 'warm', label: `${counts.warm} warm`, tone: 'warm', onClick: () => onGo('warm') });
  if (counts.cold > 0) chips.push({ key: 'cold', label: `${counts.cold} cold`, tone: 'cold', onClick: () => onGo('cold') });
  if (counts.nurture > 0) chips.push({ key: 'nurture', label: `${counts.nurture} nurture`, tone: 'nurture', onClick: () => onGo('nurture') });
  if (counts.futureProspect > 0)
    chips.push({
      key: 'future-prospect',
      label: `${counts.futureProspect} future prospect`,
      tone: 'futureProspect',
      onClick: () => onGo('future-prospect')
    });
  if (banner?.approvalPendingCount > 0)
    chips.push({ key: 'approval', label: `${banner.approvalPendingCount} approval pending`, tone: 'approval', onClick: () => onGo('approval') });
  if (banner?.ucPendingCount > 0)
    chips.push({ key: 'uc', label: `${banner.ucPendingCount} UC pending`, tone: 'uc', onClick: () => onGo('uc') });
  if (banner?.followUpOverdueCount > 0)
    chips.push({ key: 'overdue', label: `${banner.followUpOverdueCount} overdue follow-up`, tone: 'overdue', onClick: () => onGo('overdue') });
  return chips;
}

/**
 * Compact AI Brief tile that lines up with the lead metric cards and opens a
 * right-side drawer with the full brief. Use this in place of the full-width
 * banner when there is a metrics row to align with.
 */
export function LeadsAiBrief({ projectId, pipelineCounts = null, className = '' }) {
  const navigate = useNavigate();
  const apiProjectId = resolveAiProjectId(projectId);
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['aiLeadsSummary', apiProjectId],
    queryFn: () => getAiLeadsSummary(apiProjectId),
    staleTime: CACHE_STALE_MS
  });

  const banner = data?.banner;
  const counts = useMemo(
    () => ({
      hot: pipelineCounts?.hot ?? banner?.hotCount ?? 0,
      warm: pipelineCounts?.warm ?? banner?.warmCount ?? 0,
      cold: pipelineCounts?.cold ?? banner?.coldCount ?? 0,
      nurture: pipelineCounts?.nurture ?? banner?.nurtureCount ?? 0,
      futureProspect:
        pipelineCounts?.futureProspect ?? banner?.futureProspectCount ?? 0
    }),
    [banner, pipelineCounts]
  );

  const highlights = Array.isArray(banner?.highlights) ? banner.highlights : [];
  const overdue = banner?.followUpOverdueCount ?? 0;

  const openDrawer = () => {
    setOpen(true);
    requestAnimationFrame(() => setVisible(true));
  };
  const closeDrawer = () => {
    setVisible(false);
    setTimeout(() => setOpen(false), 200);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const goToLeads = (aiFilter) => {
    navigate(buildAiLeadsListPath({ aiFilter, projectId: apiProjectId }));
    closeDrawer();
  };

  const chips = buildBriefChips(banner, counts, goToLeads);

  const tileSubtitle = isLoading
    ? 'Analysing leads…'
    : isError
      ? 'Tap to retry'
      : overdue > 0
        ? `${overdue} overdue follow-up${overdue > 1 ? 's' : ''}`
        : highlights.length > 0
          ? `${highlights.length} thing${highlights.length > 1 ? 's' : ''} need attention`
          : 'All clear · tap for insights';

  return (
    <>
      <button
        type="button"
        onClick={openDrawer}
        className={`group relative h-[92px] w-full overflow-hidden rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-sky-50 px-4 text-left shadow-sm transition hover:shadow-md ${className}`}>
        <div className="flex h-full items-center gap-3">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">AI Brief</p>
            <p className="mt-0.5 truncate text-sm font-semibold text-gray-900">
              {banner?.headline || 'Lead insights'}
            </p>
            <p className="mt-0.5 truncate text-[11px] text-gray-500">{tileSubtitle}</p>
          </div>
        </div>
        <span className="absolute bottom-2 right-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600/10 text-indigo-600 transition group-hover:bg-indigo-600/20">
          <ArrowUpRight size={14} />
        </span>
      </button>

      {open
        ? createPortal(
            <div className="fixed inset-0 z-[1000] flex justify-end">
              <div
                className={`absolute inset-0 bg-black/30 transition-opacity duration-200 ${
                  visible ? 'opacity-100' : 'opacity-0'
                }`}
                onClick={closeDrawer}
              />
              <div
                className={`relative flex h-full w-[420px] max-w-full flex-col bg-white shadow-2xl transition-transform duration-200 ease-out ${
                  visible ? 'translate-x-0' : 'translate-x-full'
                }`}>
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-indigo-600" />
                    <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900">
                      AI Brief
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={closeDrawer}
                    className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                    <X size={18} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {isLoading ? (
                    <AiInsightSkeleton lines={4} />
                  ) : isError ? (
                    <AiInsightError
                      message={error?.response?.data?.message || error?.message}
                      onRetry={() => refetch()}
                    />
                  ) : !banner ? (
                    <p className="text-sm text-gray-500">No AI brief available yet.</p>
                  ) : (
                    <>
                      <p className="text-base font-semibold leading-snug text-gray-900">
                        {banner.headline}
                      </p>
                      {banner.summary ? (
                        <p className="mt-2 text-sm leading-relaxed text-gray-600">{banner.summary}</p>
                      ) : null}

                      {highlights.length > 0 ? (
                        <div className="mt-4">
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                            Needs attention
                          </p>
                          <ul className="space-y-2.5">
                            {highlights.map((item) => (
                              <li
                                key={`${item.leadId ?? item.leadName}-${item.hint}`}
                                className="flex gap-2 rounded-lg bg-amber-50/70 px-3 py-2 text-sm text-gray-700">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                                <span>
                                  <span className="font-semibold text-gray-900">{item.leadName}</span>
                                  <span className="text-gray-500"> — {item.hint}</span>
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}

                      {chips.length > 0 ? (
                        <div className="mt-5">
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                            Jump to
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {chips.map((chip) => (
                              <SummaryChip
                                key={chip.key}
                                label={chip.label}
                                tone={chip.tone}
                                onClick={chip.onClick}
                              />
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

export function useClientAiFilterLeadIds(projectId, aiFilter) {
  const apiProjectId = resolveAiProjectId(projectId);
  const enabled = isClientAiFilter(aiFilter);

  const { data: rows, isLoading, isFetching } = useQuery({
    queryKey: ['aiLeadsSummaryAll', apiProjectId],
    queryFn: () => getAllAiLeadsSummaryRows(apiProjectId),
    enabled,
    staleTime: CACHE_STALE_MS
  });

  const matchingIds = React.useMemo(() => {
    if (!enabled) return null;
    return collectClientFilterLeadIds(rows || [], aiFilter);
  }, [enabled, rows, aiFilter]);

  return {
    matchingIds,
    isLoading: enabled && (isLoading || isFetching)
  };
}

export function useLeadAiScores(projectId) {
  const apiProjectId = resolveAiProjectId(projectId);
  const { data } = useQuery({
    queryKey: ['aiLeadsSummaryAll', apiProjectId, 'scores'],
    queryFn: () => getAllAiLeadsSummaryRows(apiProjectId),
    staleTime: CACHE_STALE_MS
  });

  return useMemo(() => {
    const map = new Map();
    (data || []).forEach((row) => {
      map.set(Number(row.leadId), row);
    });
    return map;
  }, [data]);
}

/** Badge color from score only — same bands as backend scoreToCategory (not CRM pstatus). */
function aiScoreBadgeColor(score) {
  if (score >= 80) return 'bg-red-50 text-red-700 ring-red-200'; // Hot
  if (score >= 60) return 'bg-amber-50 text-amber-700 ring-amber-200'; // Warm
  if (score >= 40) return 'bg-sky-50 text-sky-700 ring-sky-200'; // Nurture
  return 'bg-gray-50 text-gray-700 ring-gray-200'; // Cold / Dead
}

export function LeadAiScoreBadge({ leadId, scoreByLeadId, aiScore }) {
  const row = scoreByLeadId?.get(Number(leadId));
  const score =
    aiScore != null && Number.isFinite(Number(aiScore))
      ? Math.round(Number(aiScore))
      : row?.score;
  if (score == null || !Number.isFinite(Number(score))) return null;

  const color = aiScoreBadgeColor(Number(score));

  return (
    <span
      className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none ring-1 transition-colors ${color} hover:brightness-95`}
      title={row?.hint ? `AI lead score · ${row.hint}` : 'AI lead score'}>
      AI {score}
    </span>
  );
}
